import { format } from 'url';
import { useEventCallback } from '@flex-packages/hooks-common';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import getPrevLocation from '../../utils/getPrevLocation.mjs';
import { getQueryFromSearchParams, convertFalsyQuery } from '../../utils/params.mjs';
import { getBasePathFromUrl } from '../../utils/basePath.mjs';
import isNextAppUrl from '../../utils/isNextAppUrl.mjs';
import useFlexLocation from '../useFlexLocation.mjs';

const usePodoFlexRouter = () => {
  const [searchParams] = useSearchParams();
  const location = useFlexLocation();
  const { pathname, search, state, hash } = location;
  const navigate = useNavigate();
  const asPath = [pathname, search].join("");
  const pathParams = useParams();
  const queryParams = getQueryFromSearchParams(searchParams);
  const rawQuery = { ...pathParams, ...queryParams };
  const basePath = getBasePathFromUrl(pathname);
  const back = () => {
    return navigate(-1);
  };
  const push = useEventCallback(
    async (url, as, options) => {
      if (as || options?.shallow) {
        console.warn(
          "flexrouter.push\uC758 as\uC640 options.shallow \uD30C\uB77C\uBBF8\uD130\uB294 deprecated \uB418\uC5C8\uC2B5\uB2C8\uB2E4."
        );
      }
      const navigateTo = typeof url === "string" ? url : format(url);
      const isTargetToNextApp = isNextAppUrl(navigateTo);
      if (isTargetToNextApp) {
        window.location.href = navigateTo;
        return;
      }
      return navigate(navigateTo, {
        replace: false,
        state: {
          ...options?.state ?? {},
          prevLocation: location
        }
      });
    }
  );
  const replace = useEventCallback(
    async (url, as, options) => {
      if (as || options?.shallow) {
        console.warn(
          "flexrouter.replace\uC758 as\uC640 options.shallow \uD30C\uB77C\uBBF8\uD130\uB294 deprecated \uB418\uC5C8\uC2B5\uB2C8\uB2E4."
        );
      }
      const navigateTo = typeof url === "string" ? url : format(url);
      const isTargetToNextApp = isNextAppUrl(navigateTo);
      if (isTargetToNextApp) {
        return window.location.replace(navigateTo);
      }
      return navigate(navigateTo, {
        replace: true,
        state: {
          ...options?.state ?? {},
          prevLocation: getPrevLocation({ replace: true, location })
        }
      });
    }
  );
  const updateQueryParams = useEventCallback(
    async (queryValue, options, as) => {
      if (as) {
        console.warn(
          "flexrouter.updateQueryParams\uC758 as \uD30C\uB77C\uBBF8\uD130\uB294 deprecated \uB418\uC5C8\uC2B5\uB2C8\uB2E4."
        );
      }
      const routeFn = options.mode === "replace" ? replace : push;
      const newQueryParams = convertFalsyQuery(
        typeof queryValue === "function" ? { ...queryValue(queryParams) } : {
          ...queryParams,
          ...queryValue
        }
      );
      return routeFn(
        {
          pathname,
          query: newQueryParams
        },
        void 0,
        {
          shallow: options.shallow,
          state: options.state
        }
      );
    }
  );
  const removeQueryParams = useEventCallback(
    (key, option, as) => {
      return updateQueryParams(
        (prev) => {
          const copiedPrevQuery = { ...prev };
          const keyList = typeof key === "string" ? [key] : key;
          keyList.forEach((it) => {
            delete copiedPrevQuery[it];
          });
          return copiedPrevQuery;
        },
        option,
        as
      );
    }
  );
  return {
    asPath,
    back,
    basePath,
    events: void 0,
    hash,
    pathname,
    pathParams,
    push,
    queryParams,
    rawQuery,
    removeQueryParams,
    replace,
    state,
    updateQueryParams
  };
};

export { usePodoFlexRouter };
//# sourceMappingURL=usePodoFlexRouter.mjs.map
